import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useSignup } from '../hooks/useUserQueries';
import ProgressIndicator from '../components/common/ProgressIndicator';
import BasicInfo from '../components/signup/BasicInfo';
import EmailVerification from '../components/signup/EmailVerification';
import Documents from '../components/signup/Documents';
import { ROUTES, STORAGE_KEYS } from '../utils/constants';
import { UserIcon, BuildingIcon } from '../components/common/Icons';
import logo from '../assets/logo.svg';
import './Signup.css';

const Signup = () => {
  const [currentStep, setCurrentStep] = useState(0); // Start with role selection
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({});
  const [signupEmail, setSignupEmail] = useState('');
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signupMutation = useSignup();
  
  const steps = [
    'Select Role',
    'Basic Info',
    'Email Verification',
    'Document Upload'
  ];

  const handleRoleSelect = (roleType) => {
    setSelectedRole(roleType);
    setFormData(prev => ({ ...prev, roleType }));
    setCurrentStep(1); // Move to basic info step
  };

  const handleBasicInfoNext = async (stepData) => {
    setError('');
    setFormData(prev => ({ ...prev, ...stepData }));
    
    try {
      // Submit signup to backend immediately after basic info
      await signupMutation.mutateAsync(stepData);
      
      // Store email for verification
      setSignupEmail(stepData.email);
      sessionStorage.setItem(STORAGE_KEYS.SIGNUP_PASSWORD, stepData.password);
      
      // Move to email verification step on success
      setCurrentStep(2);
    } catch (err) {
      // Toast notification is handled by the hook, but we can also set local error for form display
      if (err.data?.detail) {
        setError(err.data.detail);
      } else if (err.data) {
        // Handle field-specific errors
        const errorMessages = [];
        Object.keys(err.data).forEach(key => {
          const messages = Array.isArray(err.data[key]) ? err.data[key] : [err.data[key]];
          errorMessages.push(...messages);
        });
        setError(errorMessages.join('. '));
      }
    }
  };

  const handleEmailVerified = async () => {
    // Auto-login will be handled in EmailVerification component
    // After login, send user to contract gate
    navigate(ROUTES.CONTRACT, { replace: true, state: { returnToDocuments: true } });
  };

  const handleDocumentsNext = () => {
    // Navigate to login after document upload (or skip)
    navigate(ROUTES.LOGIN, { 
      state: { 
        message: 'Registration completed successfully! Please login to continue.' 
      } 
    });
  };

  const handleDocumentsSkip = () => {
    // Navigate to login after skipping documents
    navigate(ROUTES.LOGIN, { 
      state: { 
        message: 'Registration completed successfully! Please login to continue.' 
      } 
    });
  };

  const handlePrevious = () => {
    if (currentStep === 1) {
      // Go back to role selection
      setCurrentStep(0);
      setSelectedRole(null);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 0));
    }
    setError('');
  };

  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam === 'documents') {
      setCurrentStep(3);
    }
  }, [searchParams]);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="signup__role-selection">
            <h3 className="signup__role-title">Choose Your Account Type</h3>
            <p className="signup__role-subtitle">Select how you want to use Sage Locum</p>
            <div className="signup__role-options">
              <button
                type="button"
                className={`signup__role-card ${selectedRole === 'employee' ? 'signup__role-card--selected' : ''}`}
                onClick={() => handleRoleSelect('employee')}
              >
                <div className="signup__role-icon">
                  <UserIcon size={48} />
                </div>
                <h4 className="signup__role-card-title">Register as Employee</h4>
                <p className="signup__role-card-description">
                  I'm a dental professional looking for locum opportunities
                </p>
                <ul className="signup__role-features">
                  <li>Find job opportunities</li>
                  <li>Set your availability</li>
                  <li>Get matched with practices</li>
                </ul>
              </button>
              <button
                type="button"
                className={`signup__role-card ${selectedRole === 'employer' ? 'signup__role-card--selected' : ''}`}
                onClick={() => handleRoleSelect('employer')}
              >
                <div className="signup__role-icon">
                  <BuildingIcon size={48} />
                </div>
                <h4 className="signup__role-card-title">Register as Employer</h4>
                <p className="signup__role-card-description">
                  I'm a dental practice looking for locum staff
                </p>
                <ul className="signup__role-features">
                  <li>Post job opportunities</li>
                  <li>Find qualified professionals</li>
                  <li>Manage your practice needs</li>
                </ul>
              </button>
            </div>
          </div>
        );
      case 1:
        return (
          <>
            {error && (
              <div className="signup__error" role="alert">
                <svg className="signup__error-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            <BasicInfo 
              data={{ ...formData, roleType: selectedRole }} 
              onNext={handleBasicInfoNext}
              isSubmitting={signupMutation.isPending}
            />
          </>
        );
      case 2:
        return (
          <EmailVerification 
            email={signupEmail}
            password={formData.password}
            onVerified={handleEmailVerified}
          />
        );
      case 3:
        return (
          <Documents 
            data={formData}
            onNext={handleDocumentsNext}
            onSkip={handleDocumentsSkip}
              onPrevious={() => setCurrentStep(2)}
            isSubmitting={false}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 0:
        return 'Get Started';
      case 1:
        return 'Create Your Account';
      case 2:
        return 'Verify Your Email';
      case 3:
        return 'Upload Documents';
      default:
        return 'Join Sage Locum';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 0:
        return 'Select your account type to begin registration';
      case 1:
        return 'Enter your personal and professional information to get started';
      case 2:
        return 'Verify your email address to activate your account';
      case 3:
        return 'Upload your compliance documents (optional - you can skip and upload later)';
      default:
        return 'Complete your profile to start finding locum opportunities';
    }
  };

  const renderVisualContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <div className="signup__visual-icon">
              <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="roleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="choiceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                {/* Background circle */}
                <circle cx="120" cy="120" r="100" fill="url(#roleGradient)"/>
                {/* Two paths/choices */}
                <path d="M 60 100 Q 60 80 80 80 Q 100 80 120 100" stroke="url(#choiceGradient)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
                <path d="M 120 100 Q 140 80 160 80 Q 180 80 180 100" stroke="url(#choiceGradient)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
                <circle cx="80" cy="140" r="20" fill="url(#choiceGradient)" opacity="0.9"/>
                <circle cx="160" cy="140" r="20" fill="url(#choiceGradient)" opacity="0.9"/>
                <path d="M 60 160 Q 60 180 80 180 Q 100 180 120 160" stroke="url(#choiceGradient)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
                <path d="M 120 160 Q 140 180 160 180 Q 180 180 180 160" stroke="url(#choiceGradient)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
              </svg>
            </div>
            <h3 className="signup__visual-title">Choose Your Path</h3>
            <p className="signup__visual-description">
              Whether you're a dental professional seeking opportunities or a practice looking for staff, we're here to help you connect.
            </p>
            <div className="signup__features">
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>Flexible Opportunities</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span>Verified Professionals</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Secure Platform</span>
              </div>
            </div>
          </>
        );
      case 1:
        return (
          <>
            <div className="signup__visual-icon">
              <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="basicInfoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="userGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                {/* Background circle */}
                <circle cx="120" cy="120" r="100" fill="url(#basicInfoGradient)"/>
                {/* User profile icon */}
                <circle cx="120" cy="100" r="30" fill="url(#userGradient)" opacity="0.9"/>
                <path d="M 80 180 Q 80 150 120 150 Q 160 150 160 180" stroke="white" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
                {/* Form/document lines */}
                <rect x="90" y="190" width="60" height="4" rx="2" fill="white" opacity="0.7"/>
                <rect x="90" y="200" width="50" height="4" rx="2" fill="white" opacity="0.5"/>
                <rect x="90" y="210" width="55" height="4" rx="2" fill="white" opacity="0.5"/>
              </svg>
            </div>
            <h3 className="signup__visual-title">Create Your Account</h3>
            <p className="signup__visual-description">
              Get started in minutes! Provide your basic information and professional details to begin your journey with Sage Locum.
            </p>
            <div className="signup__features">
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>Personal Information</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span>Professional Details</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Secure & Private</span>
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="signup__visual-icon">
              <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="verifyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="emailGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                {/* Background circle */}
                <circle cx="120" cy="120" r="100" fill="url(#verifyGradient)"/>
                {/* Email envelope */}
                <rect x="80" y="100" width="80" height="60" rx="4" fill="url(#emailGradient)" opacity="0.9"/>
                <path d="M 80 100 L 120 130 L 160 100" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.8"/>
                {/* Checkmark circle */}
                <circle cx="120" cy="180" r="25" fill="white" opacity="0.9"/>
                <path d="M 110 180 L 115 185 L 130 170" stroke="var(--turquoise)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                {/* Verification rays */}
                <path d="M 120 60 L 120 80" stroke="white" strokeWidth="4" strokeLinecap="round" opacity="0.7"/>
                <path d="M 120 60 L 105 75" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
                <path d="M 120 60 L 135 75" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
              </svg>
            </div>
            <h3 className="signup__visual-title">Verify Your Email</h3>
            <p className="signup__visual-description">
              We've sent a verification link to your email address. Click the link to activate your account and complete your registration.
            </p>
            <div className="signup__features">
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span>Check Your Inbox</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Click Verification Link</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Start Your Journey</span>
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className="signup__visual-icon">
              <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="docGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="fileGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                {/* Background circle */}
                <circle cx="120" cy="120" r="100" fill="url(#docGradient)"/>
                {/* Document/file icon */}
                <rect x="90" y="80" width="60" height="80" rx="4" fill="url(#fileGradient)" opacity="0.9"/>
                <path d="M 90 80 L 90 100 L 110 100 L 110 80" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.8"/>
                {/* Upload arrow */}
                <path d="M 120 50 L 120 70" stroke="white" strokeWidth="4" strokeLinecap="round" opacity="0.7"/>
                <path d="M 120 50 L 105 65" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
                <path d="M 120 50 L 135 65" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
                {/* Checkmark */}
                <circle cx="120" cy="190" r="20" fill="white" opacity="0.9"/>
                <path d="M 112 190 L 117 195 L 128 184" stroke="var(--turquoise)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h3 className="signup__visual-title">Upload Documents</h3>
            <p className="signup__visual-description">
              Upload your compliance documents to complete your profile. You can skip this step and upload documents later from your profile.
            </p>
            <div className="signup__features">
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span>Required Documents</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
                <span>Optional Documents</span>
              </div>
              <div className="signup__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                </svg>
                <span>Skip & Upload Later</span>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="signup">
      <div className="signup__container">
        {/* Left Section - Form */}
        <div className="signup__form-section">
          <div className="signup__form-wrapper">
            {/* Logo */}
            <div className="signup__brand">
              <img src={logo} alt="Sage Locum Logo" className="signup__logo" />
            </div>

            {/* Step Header - Compact */}
            <div className="signup__header">
              <div className="signup__header-top">
                <h2 className="signup__title">{getStepTitle()}</h2>
                {/* Progress Indicator - Inline */}
                <div className="signup__progress-inline">
                  <ProgressIndicator 
                    currentStep={currentStep + 1} 
                    steps={steps} 
                  />
                </div>
              </div>
              <p className="signup__subtitle">{getStepDescription()}</p>
            </div>

            {/* Form Content */}
            <div className="signup__content">
              {renderCurrentStep()}
            </div>

            {/* Back Button (if not on first step) */}
            {currentStep > 0 && searchParams.get('step') !== 'documents' && (
              <div className="signup__back">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="signup__back-button"
                >
                  ← Back
                </button>
              </div>
            )}

            {/* Login Link */}
            <div className="signup__login">
              <p className="signup__login-text">
                Already have an account?{' '}
                <Link to={ROUTES.LOGIN} className="signup__login-link">
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right Section - Visual/Illustration (Step-specific content) */}
        <div className="signup__visual-section">
          <div className="signup__visual-content" key={currentStep}>
            {renderVisualContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
